import { Controller, useFormContext } from "react-hook-form";

import { oncePerChoices, OncePer } from "@/shared/domain/triggers";
import { FormSection, Radio, RadioGroup } from "@/shared/foundation/primitives";

import {
  type EventSubscriptionValues,
  type TriggerFormValues,
} from "../triggerFormSchema";

export interface RepeatsSectionProps {
  /** The subscription whose changes decide which grains are legal. */
  subscription: EventSubscriptionValues;
}

/**
 * How repeats collapse: what the trigger fires at most once per.
 *
 * Only a subscription has one — a schedule already fires once a minute — so
 * the section is absent on a schedule rather than shown empty.
 */
export function RepeatsSection({ subscription }: RepeatsSectionProps) {
  const { control } = useFormContext<TriggerFormValues>();
  const choices = oncePerChoices(subscription.events);
  // With nothing checked there are no grains to choose between, so the section
  // sends the reader back up rather than describing an empty control.
  const needsAChange = subscription.events.length === 0;

  if (needsAChange) {
    return null;
  }

  return (
    <FormSection title="How it handles repeats">
      <div
        data-testid="trigger-once-per-field"
        data-choices={choices.length}
        data-needs-change={String(needsAChange)}
      >
        <Controller
          control={control}
          name="firesOn.oncePer"
          render={({ field, fieldState }) => (
            <RadioGroup<OncePer>
              appearance="card"
              size="md"
              aria-label="Fire once per"
              value={field.value}
              onValueChange={field.onChange}
              error={fieldState.error?.message}
            >
              {choices.map((choice) => (
                <Radio
                  key={choice.value}
                  value={choice.value}
                  label={choice.label}
                  description={choice.description}
                />
              ))}
            </RadioGroup>
          )}
        />
      </div>
    </FormSection>
  );
}
